import React, { useEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { S, A, DARK } from './tokens';

type Props = {
  value: string;
  /** Called once typing pauses (and right away on Enter or clear). */
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  delay?: number;
  className?: string;
  width?: number | string;
  testId?: string;
};

const useStyles = createUseStyles({
  wrap: { position: 'relative', display: 'inline-flex', alignItems: 'center', minWidth: 0 },
  icon: { position: 'absolute', left: 10, fontSize: '18px !important', color: S.muted, pointerEvents: 'none' },
  input: {
    boxSizing: 'border-box',
    width: '100%',
    height: 36,
    padding: '0 34px 0 34px',
    border: `1px solid ${S.fieldLine}`,
    borderRadius: A.radius,
    background: A.surface,
    fontFamily: S.ui,
    fontSize: 14,
    color: S.fg,
    '&::placeholder': { color: '#8E9197' },
    '&:hover': { borderColor: '#B4BAC2' },
    '&:focus': { outline: 'none', borderColor: S.fg, boxShadow: `0 0 0 1px ${S.fg}` },
    '&::-webkit-search-cancel-button': { display: 'none' },
    [DARK]: {
      '&::placeholder': { color: S.muted },
      '&:hover': { borderColor: S.muted },
      '&:focus': { borderColor: S.fg }
    }
  },
  clear: {
    all: 'unset',
    position: 'absolute',
    right: 6,
    width: 24,
    height: 24,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    color: S.muted,
    cursor: 'pointer',
    '& .material-icons': { fontSize: 16 },
    '&:hover': { background: S.chromeHover, color: S.fg },
    '&:focus-visible': A.focus
  }
});

/**
 * A search box that searches once you stop typing, not on every key.
 * Escape clears it.
 */
const SearchField: React.FC<Props> = ({ value, onChange, placeholder = 'Search', label, delay = 300, className, width = 280, testId }) => {
  const c = useStyles();
  const [text, setText] = useState(value);
  const timer = useRef<number | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // What this field last sent: the value coming back from it isn't an outside change.
  const sent = useRef(value);

  // Follow changes from outside (back button, a "Clear search" link), not the echo of our own.
  useEffect(() => {
    if (value.trim() === sent.current.trim()) return;
    sent.current = value;
    setText(value);
  }, [value]);
  useEffect(() => () => { if (timer.current !== null) window.clearTimeout(timer.current); }, []);

  const send = (v: string) => {
    if (v.trim() === sent.current.trim()) return;
    sent.current = v.trim();
    onChangeRef.current(v.trim());
  };
  const commit = (v: string, now = false) => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    if (now) { send(v); return; }
    timer.current = window.setTimeout(() => { timer.current = null; send(v); }, delay);
  };

  return (
    <div className={[c.wrap, className].filter(Boolean).join(' ')} style={{ width }}>
      <i className={`material-icons ${c.icon}`} aria-hidden>search</i>
      <input
        type="search"
        className={c.input}
        value={text}
        placeholder={placeholder}
        aria-label={label || placeholder}
        data-testid={testId}
        onChange={(e) => { setText(e.target.value); commit(e.target.value); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(text, true); }
          if (e.key === 'Escape' && text) { e.preventDefault(); setText(''); commit('', true); }
        }}
      />
      {text && (
        <button type="button" className={c.clear} aria-label="Clear search" onClick={() => { setText(''); commit('', true); }}>
          <i className="material-icons" aria-hidden>close</i>
        </button>
      )}
    </div>
  );
};

export default SearchField;
